/* ==========================================================================
   GREEN ROOM — header animation ("ransom-note" cast headers) + scroll reveal
   + scroll cue + photo-credit positioning.

   Each .cast letter gets a random font / colour / case / tilt / shadow (and
   sometimes a contrasting border). A single timer re-rolls one random letter
   on a tick; CLICKING a header re-rolls every letter at once. Letters are
   decorative (aria-hidden + non-selectable); the real text is the .cast aria-label.

   window.GreenRoomChrome.init() is idempotent — call it again after an AJAX
   page swap to (re)wire the freshly-injected content.
   ========================================================================== */
(function () {
  var prefersReduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Loaded display fonts only (all in the <head> font <link>) so they render 100% of the time.
  var CAST_FONTS = [
    "'Pathway Gothic One',sans-serif",
    "'Libre Franklin',sans-serif",
    "'Bodoni Moda',serif",
    "'Oswald',sans-serif",
    "'Saira Condensed',sans-serif",
    "'Archivo Black',sans-serif",
    "'Inter',sans-serif",
    "'Jost',sans-serif",
    "'Anton',sans-serif",
    "'Michroma',sans-serif",
    "'Abril Fatface',serif",
    "'DM Serif Display',serif",
    "'Yeseva One',serif",
    "'Alfa Slab One',serif",
    "'UnifrakturCook',cursive"
  ];
  var CAST_COLORS = ['#025F1D', '#4AA3DB', '#C10016', '#EAAA00', '#572C5F', '#F0EEE9']; // 6 page identities
  var CAST_MODS = ['', 'bordered', 'bold', 'ital', 'bold bordered', 'ital bordered'];   // case handled separately
  var pick = function (a) { return a[Math.floor(Math.random() * a.length)]; };
  var rand = function (a, b) { return Math.floor(Math.random() * (b - a + 1)) + a; };

  var animThunks = [];   // one re-roll closure per letter — driven by a single timer
  var animTimer = null;
  var revealIO = null;
  var creditIO = null;
  var onScrollCue = null;
  var fitTimer = null;
  var faviconStarted = false;

  // Exclude the current page's bar/accent colour so letters don't blend into the tint.
  function castPalette() {
    var accent = (getComputedStyle(document.documentElement).getPropertyValue('--bar-color') || '').trim().toUpperCase();
    var cc = CAST_COLORS.filter(function (c) { return c.toUpperCase() !== accent; });
    return cc.length ? cc : CAST_COLORS.slice();
  }

  // Neighbours via the DOM (no stored grid → safe across rebuilds): siblings + the letter
  // at the same index on the other line.
  function neighbors(el) {
    var n = [];
    if (el.previousElementSibling) n.push(el.previousElementSibling);
    if (el.nextElementSibling) n.push(el.nextElementSibling);
    var line = el.parentNode;
    var idx = Array.prototype.indexOf.call(line.children, el);
    var other = line.previousElementSibling || line.nextElementSibling;
    if (other && other.classList && other.classList.contains('castline') && other.children[idx]) {
      n.push(other.children[idx]);
    }
    return n;
  }

  function reroll(el, palette) {
    // random colour, distinct from immediate neighbours where possible
    var taken = {};
    neighbors(el).forEach(function (nb) { if (nb._color) taken[nb._color] = 1; });
    var choices = palette.filter(function (c) { return !taken[c]; });
    var color = choices.length ? pick(choices) : pick(palette);
    el._color = color;
    el.style.color = color;

    el.style.fontFamily = pick(CAST_FONTS);
    el.style.zIndex = rand(0, 99);
    el.style.transform = 'rotate(' + rand(-7, 7) + 'deg)';   // pasted-cutout tilt
    el.style.webkitTextStroke = '';
    el.style.removeProperty('--bcol');
    el.style.removeProperty('--bscale');

    var mod = pick(CAST_MODS);
    var cls = 'castletter ' + mod;
    if (Math.random() < 0.5) cls += ' lower';                // ~50/50 upper/lower case mix
    if (mod.indexOf('bordered') !== -1) {                    // border via ::before — random thickness
      var border = pick(palette.filter(function (x) { return x !== color; })) || color;
      el.style.setProperty('--bcol', border);
      el.style.setProperty('--bscale', (1.03 + Math.random() * 0.07).toFixed(3));
    }
    el.className = cls;
    el.style.textShadow = rand(-5, 5) + 'px ' + rand(-5, 5) + 'px ' + rand(1, 8) + 'px rgba(0,0,0,' + (0.4 + Math.random() * 0.3).toFixed(2) + ')';
  }

  function buildLetters(cast) {
    var letters = [];
    Array.prototype.slice.call(cast.querySelectorAll('.castline')).forEach(function (line) {
      var word = (line.dataset.word || line.textContent).trim();
      line.dataset.word = word;          // keep the source word for any future rebuild
      line.textContent = '';
      Array.prototype.forEach.call(word, function (ch) {
        var s = document.createElement('span');
        s.className = 'castletter';
        s.textContent = ch;
        s.setAttribute('data-ch', ch);
        s.setAttribute('aria-hidden', 'true');   // decorative; .cast aria-label carries the real text
        line.appendChild(s);
        letters.push(s);
      });
    });
    cast.dataset.built = '1';
    return letters;
  }

  function setupCast() {
    var palette = castPalette();
    animThunks = [];
    document.querySelectorAll('.cast').forEach(function (cast) {
      var letters = (cast.dataset.built && cast.querySelector('.castletter'))
        ? Array.prototype.slice.call(cast.querySelectorAll('.castletter'))
        : buildLetters(cast);

      letters.forEach(function (el) { reroll(el, palette); });

      // Click the header to re-roll every letter (palette recomputed in case the theme was cycled).
      if (!cast._clickBound) {
        cast.addEventListener('click', function () {
          var p = castPalette();
          letters.forEach(function (el) { reroll(el, p); });
        });
        cast._clickBound = true;
      }

      letters.forEach(function (el) { animThunks.push(function () { reroll(el, castPalette()); }); });
    });
  }

  // Fit-to-width: keep the home size, but scale a .cast down if a long word would overflow.
  function fitCast() {
    document.querySelectorAll('.cast').forEach(function (cast) {
      cast.style.fontSize = '';
      var cssSize = parseFloat(getComputedStyle(cast).fontSize);
      var n = 0;
      cast.querySelectorAll('.castline').forEach(function (l) {
        n = Math.max(n, l.querySelectorAll('.castletter').length);
      });
      if (!n) return;
      var avail = document.documentElement.clientWidth * 0.90;
      cast.style.fontSize = Math.min(cssSize, avail / (n * 0.9)) + 'px';
    });
  }

  function setupReveal() {
    if (revealIO) revealIO.disconnect();
    var els = document.querySelectorAll('.reveal');
    if (prefersReduce) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('in'); revealIO.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { revealIO.observe(el); });
  }

  function setupScrollCue() {
    if (onScrollCue) { removeEventListener('scroll', onScrollCue); onScrollCue = null; }
    var cue = document.querySelector('.scroll-cue');
    if (!cue) return;
    onScrollCue = function () { cue.classList.toggle('hidden', window.scrollY > 30); };
    addEventListener('scroll', onScrollCue, { passive: true });
    onScrollCue();
  }

  // Photo credit: live (top-right) over the hero, hidden through the middle,
  // shown again (bottom-right) once the footer reveals the background photo.
  function setupPhotoCredit() {
    if (creditIO) creditIO.disconnect();
    var credit = document.querySelector('.photo-credit');
    if (!credit) return;
    var foot = document.querySelector('.site-footer');
    var heroEl = document.querySelector('.hero, .page-hero');
    var heroVis = false, footVis = false;
    function sync() {
      if (footVis) { credit.classList.remove('hidden'); credit.classList.add('at-footer'); }
      else if (heroVis) { credit.classList.remove('hidden', 'at-footer'); }
      else { credit.classList.add('hidden'); }
    }
    creditIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.target === heroEl) heroVis = e.isIntersecting;
        else if (e.target === foot) footVis = e.isIntersecting;
      });
      sync();
    }, { threshold: 0 });
    if (heroEl) creditIO.observe(heroEl);
    if (foot) creditIO.observe(foot);
    sync();
  }

  // Animated "GR" tab favicon — drawn to a canvas with random cast fonts/colours/tilt,
  // re-rolled on a timer just like the header letters.
  function setupFavicon() {
    if (faviconStarted) return;
    faviconStarted = true;
    var link = document.getElementById('gr-favicon');
    if (!link) {
      link = document.createElement('link');
      link.id = 'gr-favicon';
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    var size = 64;
    var canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    var ctx = canvas.getContext('2d');
    var chars = ['G', 'R'];
    function draw() {
      ctx.clearRect(0, 0, size, size);
      chars.forEach(function (ch, i) {
        ctx.save();
        ctx.translate(i === 0 ? size * 0.28 : size * 0.72, size * 0.52);
        ctx.rotate(rand(-8, 8) * Math.PI / 180);
        ctx.font = '700 ' + Math.round(size * 0.62) + 'px ' + pick(CAST_FONTS);
        ctx.fillStyle = '#F0EEE9';   // always cream-white (reads best on the browser tab bar)
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(Math.random() < 0.5 ? ch.toLowerCase() : ch, 0, 0);
        ctx.restore();
      });
      try { link.href = canvas.toDataURL('image/png'); } catch (e) {}
    }
    draw();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(draw);
    if (!prefersReduce) setInterval(draw, 896);   // 67 BPM (60000/67)
  }

  function init() {
    if (document.body) document.body.classList.toggle('motion-paused', prefersReduce);
    setupCast();
    fitCast();
    setupReveal();
    setupScrollCue();
    setupPhotoCredit();
    setupFavicon();
    if (!prefersReduce && animThunks.length && !animTimer) {
      animTimer = setInterval(function () {
        if (!animThunks.length) return;
        animThunks[Math.floor(Math.random() * animThunks.length)]();
      }, 896);   // 67 BPM (60000/67)
    }
  }

  window.GreenRoomChrome = { init: init, fit: fitCast };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitCast);
  addEventListener('resize', function () { clearTimeout(fitTimer); fitTimer = setTimeout(fitCast, 150); });
})();
